import { LegalLayout } from '@/components/layout';
import { SEO } from '@/components/SEO';

export function PrivacyPolicyPage() {
  return (
    <>
    <SEO
      title="Privacy Policy - Vettis"
      description="Vettis Privacy Policy. Learn how we collect, use, and protect your data with secure encryption and transparent privacy practices."
      path="/privacy"
    />
    <LegalLayout title="Privacy Policy" lastUpdated="January 11, 2026">
      <section className="space-y-6">
        <div className="bg-surface rounded-lg p-6 border border-border">
          <p className="text-text-secondary">
            This Privacy Policy describes how Vettis ("we," "us," or "our") collects, uses, and protects your personal information when you use our AI-powered research service.
          </p>
        </div>

        <div className="space-y-8">
          {/* Information We Collect */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">1. Information We Collect</h2>
            <div className="space-y-4 text-text-secondary">
              <h3 className="text-lg font-medium text-text-primary">1.1 Account Information</h3>
              <p>When you create an account, we collect:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Email address</li>
                <li>Name (optional)</li>
                <li>Password (securely hashed)</li>
                <li>Timezone and delivery preferences</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">1.2 Subscription and Payment Information</h3>
              <p>When you subscribe to our service:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Payment information is processed securely through Stripe</li>
                <li>We do not store complete credit card numbers</li>
                <li>We retain billing history and subscription status</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">1.3 Research Preferences and Content</h3>
              <p>To personalize your research, we collect:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Research topics and interests you select</li>
                <li>Custom prompts you create</li>
                <li>Uploaded documents (for Premium users)</li>
                <li>Feedback you provide on research content</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">1.4 Usage Data</h3>
              <p>We automatically collect:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Research engagement metrics</li>
                <li>Service access times and frequency</li>
                <li>Device and browser information</li>
                <li>IP address (for security purposes)</li>
              </ul>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">2. How We Use Your Information</h2>
            <div className="space-y-4 text-text-secondary">
              <h3 className="text-lg font-medium text-text-primary">2.1 Service Delivery</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Generate personalized AI research verdicts</li>
                <li>Deliver research at your preferred time</li>
                <li>Process subscription payments</li>
                <li>Provide customer support</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">2.2 AI Processing</h3>
              <p className="bg-primary/10 p-4 rounded-lg border border-primary/20">
                <strong className="text-primary">Important:</strong> Your research preferences and custom prompts are sent to third-party AI providers (including OpenAI, Anthropic, and Google) to generate your personalized content. These providers process your data according to their respective privacy policies and data processing agreements.
              </p>

              <h3 className="text-lg font-medium text-text-primary mt-6">2.3 Service Improvement</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Analyze usage patterns to improve our service</li>
                <li>Develop new features based on user feedback</li>
                <li>Ensure service reliability and security</li>
              </ul>
            </div>
          </div>

          {/* Data Sharing */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">3. Data Sharing and Third Parties</h2>
            <div className="space-y-4 text-text-secondary">
              <p>We share your information with the following third parties:</p>

              <h3 className="text-lg font-medium text-text-primary mt-4">3.1 AI Service Providers</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>OpenAI</strong> - For GPT-4 based research generation</li>
                <li><strong>Anthropic</strong> - For Claude-based analysis</li>
                <li><strong>Google</strong> - For Gemini-based insights</li>
              </ul>
              <p className="text-sm mt-2">These providers may process your prompts and preferences to generate content. They operate under their own privacy policies and data retention practices.</p>

              <h3 className="text-lg font-medium text-text-primary mt-6">3.2 Service Providers</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>Stripe</strong> - Payment processing</li>
                <li><strong>Amazon Web Services</strong> - Cloud hosting and storage</li>
                <li><strong>Email delivery services</strong> - Email notifications</li>
              </ul>

              <h3 className="text-lg font-medium text-text-primary mt-6">3.3 We Do Not</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Sell your personal information to third parties</li>
                <li>Share your data for advertising purposes</li>
                <li>Provide access to your data without your consent (except as required by law)</li>
              </ul>
            </div>
          </div>

          {/* Data Security */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">4. Data Security</h2>
            <div className="space-y-4 text-text-secondary">
              <p>We implement appropriate security measures to protect your data:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Encryption in transit (TLS/SSL) and at rest</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>Secure cloud infrastructure</li>
              </ul>
            </div>
          </div>

          {/* Data Retention */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">5. Data Retention</h2>
            <div className="space-y-4 text-text-secondary">
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>Account data:</strong> Retained while your account is active</li>
                <li><strong>Research history:</strong> Stored for 90 days</li>
                <li><strong>Uploaded documents:</strong> Deleted upon request or account closure</li>
                <li><strong>Payment records:</strong> Retained as required by law (typically 7 years)</li>
              </ul>
              <p className="mt-4">Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law.</p>
            </div>
          </div>

          {/* Your Rights */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">6. Your Rights</h2>
            <div className="space-y-4 text-text-secondary">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Export:</strong> Download your data in a portable format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p className="mt-4">To exercise these rights, contact us at <a href="mailto:contact@vettis.ai" className="text-primary hover:underline">contact@vettis.ai</a></p>
            </div>
          </div>

          {/* Children's Privacy */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">7. Children's Privacy</h2>
            <div className="space-y-4 text-text-secondary">
              <p>Our service is not intended for users under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us.</p>
            </div>
          </div>

          {/* International Transfers */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">8. International Data Transfers</h2>
            <div className="space-y-4 text-text-secondary">
              <p>Your data may be transferred to and processed in countries other than your own, including the United States. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.</p>
            </div>
          </div>

          {/* Changes */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">9. Changes to This Policy</h2>
            <div className="space-y-4 text-text-secondary">
              <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through our service. Your continued use of the service after changes constitutes acceptance of the updated policy.</p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h2 className="text-2xl font-semibold text-text-primary mb-4">10. Contact Us</h2>
            <div className="space-y-4 text-text-secondary">
              <p>If you have questions about this Privacy Policy or our data practices, contact us at:</p>
              <div className="bg-surface rounded-lg p-4 border border-border">
                <p><strong>Vettis</strong></p>
                <p>Email: <a href="mailto:contact@vettis.ai" className="text-primary hover:underline">contact@vettis.ai</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </LegalLayout>
    </>
  );
}

